import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { authApi } from '@/lib/api/auth.api'
import {
  LoginRequest,
  SignupRequest,
  User,
} from '@/lib/types/auth.types'
import { toast } from '@/hooks/use-toast'
import { useAuthContext } from '@/lib/context/auth-context'
import { requestForToken, deleteFCMToken } from '@/lib/firebase'

/**
 * Hook for login mutation
 */
export function useLogin() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { setUser, setToken } = useAuthContext()

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const fcmToken = await requestForToken();
      return authApi.login({ ...data, fcm_token: fcmToken || undefined });
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        // Store token and user in localStorage
        localStorage.setItem('auth_token', response.data.access_token)
        localStorage.setItem('auth_user', JSON.stringify(response.data.user))

        // Update AuthContext immediately to avoid redirect race conditions
        try {
          setToken(response.data.access_token)
          setUser(response.data.user)
        } catch (err) {
          console.warn('Error updating auth context:', err)
        }

        // Update query cache
        queryClient.setQueryData(['currentUser'], response.data.user)

        // Show success toast
        toast({
          title: response.message.title || 'Login Successful',
          description: response.message.description || 'Welcome back!',
        })

        // Redirect to intended route if present
        let redirectTo: string | null = null
        try {
          redirectTo = sessionStorage.getItem('redirectAfterLogin')
          if (redirectTo) sessionStorage.removeItem('redirectAfterLogin')
        } catch (err) {
          console.warn('Error updating auth context:', err)
        }
        router.push(redirectTo || '/dashboard')
      }
    },
    onError: (error: any, variables) => {
      const errorMessage =
        error.response?.data?.message?.description ||
        'Login failed. Please check your credentials.'

      toast({
        title: error.response?.data?.message?.title || 'Login Failed',
        description: errorMessage,
        variant: 'destructive',
      })

      // If user is not verified, redirect to email verification page and create session for email verification
      if (error?.response?.status === 403 && error?.response?.data?.data?.is_verified === false) {

        // Persist user_id for email verification flow
        sessionStorage.setItem("email_verify_user_id", error?.response?.data?.data?.user_id)

        // Persist email for resend OTP flow
        const emailFromResponse = error?.response?.data?.data?.email
        const emailToPersist = emailFromResponse || variables?.email
        if (emailToPersist) {
          sessionStorage.setItem('email_verify_email', emailToPersist)
        }

        router.push('/email-verification')
      }
    },
  })
}

/**
 * Hook for resending verification OTP
 */
export function useResendVerificationOtp() {
  return useMutation({
    mutationFn: ({
      user_id,
      email,
    }: {
      user_id: string
      email: string
    }) => authApi.resendVerificationOtp(user_id, email),
    onSuccess: (response) => {
      toast({
        title: response.message?.title || 'OTP Sent',
        description:
          response.message?.description ||
          'A new verification code has been sent to your email.',
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Resend Failed',
        description:
          error.response?.data?.message?.description ||
          'Failed to resend verification code.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook for signup mutation
 */
export function useSignup() {
  const router = useRouter()

  return useMutation({
    mutationFn: (data: SignupRequest) => authApi.signup(data),
    onSuccess: (response, variables) => {
      if (response.success) {
        toast({
          title: response.message.title || 'Signup Successful',
          description:
            response.message.description ||
            'Please check your email to verify your account.',
        })
        // Persist user_id for email verification flow
        if (response.data && (response.data as any).user_id) {
          sessionStorage.setItem(
            "email_verify_user_id",
            (response.data as any).user_id
          );
        }

        // Set flag for subscription welcome modal after login
        localStorage.setItem('just_signed_up', 'true')

        // Persist email for resend OTP flow
        const emailFromResponse = (response.data as any)?.email
        const emailToPersist = emailFromResponse || variables?.email
        if (emailToPersist) {
          sessionStorage.setItem('email_verify_email', emailToPersist)
        }

        // Redirect to email verification page
        router.push('/email-verification')
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message?.description ||
        'Signup failed. Please try again.'

      toast({
        title: error.response?.data?.message?.title || 'Signup Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { clearAuth } = useAuthContext()

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: async (response) => {
      await deleteFCMToken();
      clearAuth()
      // Clear all queries
      queryClient.clear()

      toast({
        title: response.message?.title || 'Logged Out',
        description: response.message?.description || 'You have been successfully logged out.',
      })

      // Redirect to login
      router.push('/login')
    },
    onError: () => {
      // Even if API call fails, clear local data
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      queryClient.clear()
      router.push('/login')
    },
  })
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  const { token } = useAuthContext()

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      // First check localStorage
      if (typeof window !== 'undefined') {
        const storedUser = localStorage.getItem('auth_user')
        if (storedUser) {
          return JSON.parse(storedUser) as User
        }
      }

      // If not in localStorage, fetch from API
      const response = await authApi.getCurrentUser()
      if (response.success && response.data) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth_user', JSON.stringify(response.data))
        }
        return response.data
      }

      return null
    },
    enabled: !!token, // Only run if token exists in context
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Hook to check if user is authenticated
 */
export function useAuth() {
  const { data: user, isLoading } = useCurrentUser()
  const logout = useLogout()

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    logout: logout.mutate,
  }
}

/**
 * Hook for email verification
 */
export function useVerifyEmail() {
  const router = useRouter()

  return useMutation({
    mutationFn: ({
      user_id,
      otp,
    }: {
      user_id: string
      otp: string
    }) => authApi.verifyEmail(user_id, otp),
    onSuccess: (response) => {
      toast({
        title: response.message?.title || 'Email Verified',
        description: response.message?.description || 'Your email has been verified successfully.',
      })
      sessionStorage.removeItem('email_verify_user_id')
      sessionStorage.removeItem('email_verify_email')

      // Show success modal on login page
      sessionStorage.setItem('show_verification_welcome_modal', 'true')
      router.push('/login')
    },
    onError: (error: any) => {
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.message?.description || 'Email verification failed.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook for forgot password
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: (response) => {
      toast({
        title: response.message?.title || 'Email Sent',
        description:
          response.message?.description ||
          'Password reset instructions have been sent to your email.',
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Request Failed',
        description:
          error.response?.data?.message?.description ||
          'Failed to send password reset email.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook for reset password
 */
export function useResetPassword() {
  const router = useRouter()

  return useMutation({
    mutationFn: ({
      reset_token,
      new_password,
    }: {
      reset_token: string
      new_password: string
    }) => authApi.resetPassword(reset_token, new_password),
    onSuccess: (response) => {
      toast({
        title: response.message?.title || 'Password Reset',
        description:
          response.message?.description ||
          'Your password has been reset successfully.',
      })
      router.push('/login')
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Reset Failed',
        description:
          error.response?.data?.message?.description ||
          'Failed to reset password.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to save FCM token
 */
export function useSaveFCMToken() {
  return useMutation({
    mutationFn: ({ token, role }: { token: string; role: 'admin' | 'educator' }) => {
      if (role === 'admin') {
        return authApi.saveAdminFCMToken(token);
      }
      return authApi.saveEducatorFCMToken(token);
    },
    onSuccess: () => {
      console.log('FCM token saved successfully');
    },
    onError: (error) => {
      console.error('Failed to save FCM token:', error);
    },
  });
}
